import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { apiRequest } from "@mhj/shared/api";
import { useAuth } from "@mhj/shared/auth";
import { Card, buttonClass, secondaryButtonClass } from "../lib/ui";
import type { EmpowermentConfig } from "../lib/types";

export default function Landing() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["empowerment", "config"],
    queryFn: () => apiRequest<EmpowermentConfig>("/empowerment/config"),
  });

  const open = !!data?.cycle;
  const categories = data?.categories ?? [];
  const tracks = data?.tracks ?? [];

  return (
    <div className="space-y-5">
      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-blue-600">
          Lapai Local Government
        </p>
        <h1 className="mt-2 text-2xl font-bold leading-tight text-slate-900 sm:text-3xl">
          Apply for skills training, business capital, or equipment
        </h1>
        <p className="mt-3 text-base text-slate-600">
          The Jantabo Impact Empowerment Programme supports people across Lapai
          who want to learn a trade, start or grow a business, or work their
          land better.
        </p>

        {isLoading ? (
          <p className="mt-5 text-sm text-slate-500">Checking whether applications are open…</p>
        ) : open ? (
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <Link to={user ? "/apply" : "/register"} className={buttonClass}>
              Apply now
            </Link>
            <Link to="/status" className={secondaryButtonClass}>
              Check my status
            </Link>
            <Link to="/results" className={secondaryButtonClass}>
              Results
            </Link>
          </div>
        ) : (
          <div className="mt-5 rounded-2xl bg-amber-50 p-4">
            <p className="font-medium text-amber-900">Applications are closed</p>
            <p className="mt-1 text-sm text-amber-800">
              {data?.message ?? "The next round opens soon. Check back later."}
            </p>
            <Link to="/status" className="mt-3 inline-block text-sm font-medium text-amber-900 underline">
              Check an existing application
            </Link>
          </div>
        )}
      </section>

      {open && (
        <Card title="What you can apply for">
          <div className="space-y-4">
            {categories.map((category) => {
              const categoryTracks = tracks.filter((t) => t.categoryId === category.id);
              if (categoryTracks.length === 0) return null;
              return (
                <div key={category.id}>
                  <div className="flex items-center gap-2">
                    <span className="text-xl" aria-hidden="true">{category.icon}</span>
                    <h3 className="font-semibold text-slate-900">{category.name}</h3>
                  </div>
                  {category.description && (
                    <p className="mt-0.5 text-sm text-slate-600">{category.description}</p>
                  )}
                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {categoryTracks.map((track) => (
                      <li
                        key={track.id}
                        className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700"
                      >
                        {track.name}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card title="Before you start">
        <ul className="space-y-2.5 text-sm text-slate-700">
          <li className="flex gap-2">
            <span aria-hidden="true">•</span>
            <span>You must live in Lapai Local Government, Niger State.</span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden="true">•</span>
            <span>
              Have a phone number you can receive a text message on. We send your
              application ID and your result by SMS.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden="true">•</span>
            <span>
              Have a passport photograph, a government ID or NIN slip, and either an
              indigene certificate or an attestation letter from your ward head,
              district head, or an LGA officer.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden="true">•</span>
            <span>
              You can photograph the documents with your phone. It takes about ten
              minutes, and your answers are saved as you go.
            </span>
          </li>
        </ul>
      </Card>
    </div>
  );
}
